import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, CalendarClock, MessageCircle, Copy, Mail, StickyNote, ClipboardList, ListChecks, MapPin, Send, Zap } from "lucide-react";
import { SurveyQuestionsDialog } from "@/components/SurveyQuestionsDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_META = {
  new: ["New", "bg-blue-500/20 text-blue-300 border-blue-500/40"],
  scheduled: ["Scheduled", "bg-indigo-500/20 text-indigo-300 border-indigo-500/40"],
  confirmed: ["Confirmed", "bg-cyan-500/20 text-cyan-300 border-cyan-500/40"],
  attended: ["Attended", "bg-emerald-500/20 text-emerald-300 border-emerald-500/40"],
  no_show: ["No-show", "bg-red-500/20 text-red-300 border-red-500/40"],
  postponed: ["Postponed", "bg-amber-500/20 text-amber-300 border-amber-500/40"],
  followed_up: ["Followed up", "bg-teal-500/20 text-teal-300 border-teal-500/40"],
  converted: ["Converted", "bg-green-600/30 text-green-300 border-green-600/50"],
  lost: ["Lost", "bg-slate-600/30 text-slate-300 border-slate-600/50"],
};
const FILTERS = [["active", "Active"], ["new", "New"], ["scheduled", "Scheduled"], ["confirmed", "Confirmed"], ["attended", "Attended"], ["no_show", "No-show"], ["all", "All"]];
const openWa = (phone, text) => window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, "_blank");
const copy = async (t) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(t);
    } else {
      const ta = document.createElement("textarea");
      ta.value = t; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
    }
    toast.success("Copied");
  } catch (_) {
    try {
      const ta = document.createElement("textarea");
      ta.value = t; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
      toast.success("Copied");
    } catch (e) {
      toast.error("Copy failed — please copy manually");
    }
  }
};
const sel = "bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-white w-full";

export default function AdminTrials() {
  const [rows, setRows] = useState([]);
  const [stage, setStage] = useState("active");
  const [loading, setLoading] = useState(true);
  const [venuesOpen, setVenuesOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [dialog, setDialog] = useState(null); // {type, trial}

  const load = async (st = stage) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/academy/trials?stage=${st}`, { withCredentials: true });
      setRows(r.data.rows || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load trials"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const [wa, setWa] = useState(null);
  const [questionsOpen, setQuestionsOpen] = useState(false);
  const loadWa = React.useCallback(() => {
    axios.get(`${API}/academy/trials/twilio-status`, { withCredentials: true })
      .then((r) => setWa(r.data)).catch(() => setWa({ configured: false }));
  }, []);
  useEffect(() => { loadWa(); }, [loadWa]);
  const refresh = () => { load(stage); setDialog(null); };

  return (
    <div className="min-h-screen bg-slate-950 text-white p-4 sm:p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <CalendarClock className="w-6 h-6 text-cyan-400" />
            <h1 className="text-2xl font-bold">Trials Pipeline</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="border-slate-600" onClick={() => setVenuesOpen(true)} data-testid="trials-venues-btn"><MapPin className="w-4 h-4 mr-1" />Venues</Button>
            <Button size="sm" variant="outline" className="border-slate-600" onClick={() => setNotesOpen(true)} data-testid="trials-notes-btn"><StickyNote className="w-4 h-4 mr-1" />Messages &amp; templates</Button>
            <Button size="sm" variant="outline" className="border-slate-600" onClick={() => setQuestionsOpen(true)} data-testid="trials-questions-btn"><ListChecks className="w-4 h-4 mr-1" />Survey questions</Button>
            <Link to="/admin/trial-surveys" data-testid="trials-surveys-link">
              <Button size="sm" variant="outline" className="border-slate-600"><ClipboardList className="w-4 h-4 mr-1" />Survey results</Button>
            </Link>
            <Link to="/admin" className="text-slate-400 hover:text-white flex items-center gap-1 text-sm" data-testid="trials-back"><ArrowLeft className="w-4 h-4" />Admin</Link>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {FILTERS.map(([v, l]) => (
            <Button key={v} size="sm" variant={stage === v ? "default" : "outline"} className={stage === v ? "bg-cyan-600 hover:bg-cyan-500" : "border-slate-600"} onClick={() => { setStage(v); load(v); }} data-testid={`trials-filter-${v}`}>{l}</Button>
          ))}
        </div>

        <WhatsAppStrip wa={wa} onRefresh={loadWa} onOpenTemplates={() => setNotesOpen(true)} />

        {loading ? <p className="text-slate-400" data-testid="trials-loading">Loading…</p>
          : rows.length === 0 ? <Card className="bg-slate-900/40 border-slate-700"><CardContent className="py-10 text-center text-slate-400" data-testid="trials-empty">No trials here.</CardContent></Card>
          : <div className="space-y-3">{rows.map((t) => <TrialCard key={t.id} t={t} wa={wa} onAction={(type) => setDialog({ type, trial: t })} onSent={refresh} />)}</div>}
      </div>

      {dialog?.type === "schedule" && <ScheduleDialog trial={dialog.trial} onClose={() => setDialog(null)} onDone={refresh} />}
      {dialog?.type === "messages" && <MessagesDialog trial={dialog.trial} onClose={() => setDialog(null)} onDone={refresh} />}
      {dialog?.type === "note" && <NoteDialog trial={dialog.trial} onClose={() => setDialog(null)} onDone={refresh} />}
      {dialog?.type === "outcome" && <OutcomeDialog trial={dialog.trial} onClose={() => setDialog(null)} onDone={refresh} />}
      {dialog?.type === "survey" && <SurveyDialog trial={dialog.trial} onClose={() => setDialog(null)} onDone={refresh} />}
      {dialog?.type === "decline" && <DeclineDialog trial={dialog.trial} onClose={() => setDialog(null)} onDone={refresh} />}
      {dialog?.type === "convert" && <ConvertDialog trial={dialog.trial} onClose={() => setDialog(null)} onDone={refresh} />}
      {venuesOpen && <VenuesDialog onClose={() => setVenuesOpen(false)} />}
      {notesOpen && <MessageNotesDialog onClose={() => setNotesOpen(false)} />}
      {questionsOpen && <SurveyQuestionsDialog onClose={() => setQuestionsOpen(false)} />}
    </div>
  );
}

function WhatsAppStrip({ wa, onRefresh, onOpenTemplates }) {
  if (!wa) return null;
  const connected = !!wa.configured;
  const bits = [
    connected ? "Connected" : "Not connected",
    wa.has_confirmation_template ? "Confirmation approved" : "Confirmation not approved yet",
    wa.has_survey_template ? "Survey approved" : "Survey waiting on approval",
  ];
  return (
    <div className={`mb-4 rounded-lg border p-3 ${connected ? "border-emerald-700/40 bg-emerald-500/5" : "border-amber-600/40 bg-amber-500/5"}`}
      data-testid="trials-whatsapp-strip">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-semibold flex items-center gap-2">
            <MessageCircle className={`w-4 h-4 ${connected ? "text-emerald-400" : "text-amber-400"}`} />
            WhatsApp (Twilio)
            <span className={`text-[11px] px-2 py-0.5 rounded-full ${connected ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-200"}`}
              data-testid="trials-whatsapp-state">{bits[0]}</span>
          </p>
          <p className="text-xs text-slate-400 mt-1" data-testid="trials-whatsapp-detail">
            {bits.slice(1).join(" · ")}
            {wa.sender ? ` · sending from ${wa.sender}` : ""}
          </p>
          <p className="text-[11px] text-slate-500 mt-0.5">
            {wa.has_confirmation_template
              ? "Each trial below has a Send on WhatsApp button — one click, it goes straight out."
              : "Until the wording is approved, the green buttons open WhatsApp with the message ready to paste."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" className="h-7 text-[11px] text-slate-300"
            onClick={onRefresh} data-testid="trials-whatsapp-refresh">Check again</Button>
          <Button size="sm" variant="outline" className="h-7 text-[11px] border-slate-600"
            onClick={onOpenTemplates} data-testid="trials-whatsapp-templates">Templates</Button>
        </div>
      </div>
    </div>
  );
}

function TrialCard({ t, wa, onAction, onSent }) {
  const [label, cls] = STAGE_META[t.pipeline_stage] || STAGE_META.new;
  const scheduled = ["scheduled", "confirmed", "attended", "no_show", "postponed", "followed_up", "converted"].includes(t.pipeline_stage);
  const [sending, setSending] = useState(false);
  const attended = t.pipeline_stage === "attended";
  const wantSurvey = attended;
  const templateReady = wantSurvey ? wa?.has_survey_template : wa?.has_confirmation_template;
  const blocked = !wa?.configured ? "WhatsApp isn't connected yet"
    : !templateReady ? "The wording is still waiting on WhatsApp's approval"
      : !t.parent_phone ? "No mobile number on this parent"
        : !scheduled ? "Schedule the trial first" : null;

  const sendNow = async () => {
    setSending(true);
    try {
      if (wantSurvey) {
        await axios.post(`${API}/academy/trials/${t.id}/send-survey`,
          { channel: "twilio_whatsapp" }, { withCredentials: true });
        toast.success(`Survey sent to ${t.parent_name} on WhatsApp`);
      } else {
        await axios.post(`${API}/academy/trials/${t.id}/send-confirmation`,
          { channel: "twilio_whatsapp" }, { withCredentials: true });
        toast.success(`Confirmation sent to ${t.parent_name} on WhatsApp`);
      }
      onSent && onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "WhatsApp send failed");
    } finally { setSending(false); }
  };

  return (
    <Card className="bg-slate-900/40 border-slate-700" data-testid={`trial-card-${t.id}`}>
      <CardContent className="py-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold">{t.child_name}</span>
              {t.age_group_label && <Badge className="bg-slate-700 text-slate-200">{t.age_group_label}</Badge>
              }<Badge className={cls} data-testid={`trial-stage-${t.id}`}>{label}</Badge>
            </div>
            <p className="text-xs text-slate-400 mt-1" data-testid={`trial-dob-${t.id}`}>
              {t.child_dob ? `Born ${t.child_dob}${ageFrom(t.child_dob) ? ` (${ageFrom(t.child_dob)})` : ""}` : "DOB not given"}
              {t.child_gender ? ` · ${t.child_gender}` : ""}
            </p>
            <p className="text-xs text-slate-400">{t.experience_level || "exp n/a"} · Parent: {t.parent_name} ({t.parent_phone})</p>
            {t.preferred_slot && <p className="text-xs text-slate-500">Prefers: {t.preferred_slot}{t.signup_notes ? ` · ${t.signup_notes}` : ""}</p>}
            {t.trial_date && <p className="text-xs text-cyan-300 mt-1">Trial: {t.trial_date} {t.trial_time || ""} · {t.assigned_coach_name || "coach TBC"} · {t.venue_name || "venue TBC"}</p>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" className="h-7 text-xs bg-cyan-600 hover:bg-cyan-500" onClick={() => onAction("schedule")} data-testid={`trial-schedule-${t.id}`}><CalendarClock className="w-3 h-3 mr-1" />{scheduled ? "Reschedule" : "Schedule"}</Button>
          <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-500 disabled:opacity-40"
            disabled={!!blocked || sending} title={blocked || ""} onClick={sendNow}
            data-testid={`trial-wa-send-${t.id}`}>
            <MessageCircle className="w-3 h-3 mr-1" />
            {sending ? "Sending…" : wantSurvey ? "Send survey on WhatsApp" : "Send on WhatsApp"}
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-xs border-slate-600" disabled={!scheduled} onClick={() => onAction("messages")} data-testid={`trial-messages-${t.id}`}><MessageCircle className="w-3 h-3 mr-1" />Send / Handoff</Button>
          <Button size="sm" variant="outline" className="h-7 text-xs border-slate-600" onClick={() => onAction("note")} data-testid={`trial-note-${t.id}`}><StickyNote className="w-3 h-3 mr-1" />Note</Button>
          <Button size="sm" variant="outline" className="h-7 text-xs border-slate-600" onClick={() => onAction("outcome")} data-testid={`trial-outcome-${t.id}`}>Outcome</Button>
          <Button size="sm" variant="outline" className="h-7 text-xs border-slate-600" disabled={t.pipeline_stage !== "attended"} onClick={() => onAction("survey")} data-testid={`trial-survey-${t.id}`}><ClipboardList className="w-3 h-3 mr-1" />Survey</Button>
          <Button size="sm" variant="outline" className="h-7 text-xs border-emerald-500/50 text-emerald-200 hover:bg-emerald-500/10" onClick={() => onAction("convert")} data-testid={`trial-convert-${t.id}`}>Register for term</Button>
          <Button size="sm" variant="outline" className="h-7 text-xs border-rose-500/50 text-rose-300 hover:bg-rose-500/10" onClick={() => onAction("decline")} data-testid={`trial-decline-${t.id}`}>Decline</Button>
        </div>
        {blocked && scheduled && (
          <p className="text-[11px] text-amber-400/90" data-testid={`trial-wa-blocked-${t.id}`}>
            WhatsApp send unavailable — {blocked}. Use Send / Handoff to copy or email it.
          </p>
        )}
        {t.activity_log?.length > 0 && (
          <details className="text-xs text-slate-400" data-testid={`trial-log-${t.id}`}>
            <summary className="cursor-pointer">Activity ({t.activity_log.length})</summary>
            <ul className="mt-1 space-y-0.5">{t.activity_log.slice(-6).map((a, i) => <li key={i}>• {a.text} <span className="text-slate-600">— {a.by}</span></li>)}</ul>
          </details>
        )}
      </CardContent>
    </Card>
  );
}

function ScheduleDialog({ trial, onClose, onDone }) {
  const [teams, setTeams] = useState([]);
  const [dates, setDates] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [venues, setVenues] = useState([]);
  const [teamId, setTeamId] = useState(trial.assigned_team_id || "");
  const [coachId, setCoachId] = useState(trial.assigned_coach_id || "");
  const [trialDate, setTrialDate] = useState(trial.trial_date || "");
  const [trialTime, setTrialTime] = useState(trial.trial_time || "");
  const [venueId, setVenueId] = useState(trial.venue_id || "");
  const [sendConfirm, setSendConfirm] = useState(true);
  const [wa, setWa] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/academy/trials/twilio-status`, { withCredentials: true })
      .then((r) => setWa(r.data)).catch(() => {});
    axios.get(`${API}/academy/trials/teams?age_group=${encodeURIComponent(trial.age_group_label || "")}`, { withCredentials: true }).then((r) => setTeams(r.data.teams || []));
    axios.get(`${API}/academy/trials/coaches`, { withCredentials: true }).then((r) => setCoaches(r.data.coaches || []));
    axios.get(`${API}/academy/trial-venues`, { withCredentials: true }).then((r) => setVenues(r.data.venues || []));
  }, []); // eslint-disable-line
  useEffect(() => {
    if (!teamId) { setDates([]); return; }
    axios.get(`${API}/academy/trials/team-dates?team_id=${teamId}`, { withCredentials: true }).then((r) => setDates(r.data.dates || []));
    const tm = teams.find((x) => x.id === teamId);
    if (tm?.coach_id && !coachId) setCoachId(tm.coach_id);
  }, [teamId]); // eslint-disable-line

  const save = async () => {
    if (!trialDate) { toast.error("Pick a trial date"); return; }
    setBusy(true);
    try {
      const coach = coaches.find((c) => c.id === coachId);
      const r = await axios.post(`${API}/academy/trials/${trial.id}/schedule`, {
        trial_date: trialDate, trial_time: trialTime, team_id: teamId || null,
        coach_id: coachId || null, coach_name: coach?.name || null, venue_id: venueId || null,
        send_confirmation: sendConfirm,
      }, { withCredentials: true });
      const c = r.data.confirmation || {};
      const bits = [
        c.email ? "parent emailed" : null,
        c.whatsapp ? "WhatsApp sent" : null,
        r.data.coach_notified ? "coach told in the portal" : null,
      ].filter(Boolean);
      toast.success(`Trial scheduled${bits.length ? ` · ${bits.join(" · ")}` : ""}`);
      if (sendConfirm && !c.email && !c.whatsapp) {
        toast.warning("Nothing sent to the parent — check the email address"
          + (c.error ? `: ${c.error}` : ""));
      }
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to schedule"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white">
        <DialogHeader><DialogTitle>Schedule trial — {trial.child_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-xs text-slate-400">Team / slot ({trial.age_group_label || "age n/a"})</label>
            <select className={sel} value={teamId} onChange={(e) => setTeamId(e.target.value)} data-testid="schedule-team">
              <option value="">Select team…</option>
              {teams.map((t) => <option key={t.id} value={t.id}>{t.name} {t.coach_name ? `· ${t.coach_name}` : ""}</option>)}
            </select>
          </div>
          <div><label className="text-xs text-slate-400">Trial date {dates.length > 0 ? "(next available for this team)" : ""}</label>
            {dates.length > 0 ? (
              <select className={sel} value={trialDate} onChange={(e) => { const d = dates.find((x) => x.date === e.target.value); setTrialDate(e.target.value); if (d?.start_time) setTrialTime(d.start_time); }} data-testid="schedule-date">
                <option value="">Select date…</option>
                {dates.map((d) => <option key={d.date} value={d.date}>{d.day_of_week || ""} {d.date} {d.start_time || ""} @ {d.location || ""}</option>)}
              </select>
            ) : (
              <Input type="date" className="bg-slate-800 border-slate-700" value={trialDate} onChange={(e) => setTrialDate(e.target.value)} data-testid="schedule-date-manual" />
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-xs text-slate-400">Time</label><Input className="bg-slate-800 border-slate-700" value={trialTime} onChange={(e) => setTrialTime(e.target.value)} placeholder="17:00" data-testid="schedule-time" /></div>
            <div><label className="text-xs text-slate-400">Venue</label>
              <select className={sel} value={venueId} onChange={(e) => setVenueId(e.target.value)} data-testid="schedule-venue">
                <option value="">Select…</option>
                {venues.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
          </div>
          <div><label className="text-xs text-slate-400">Coach (suggested from team — change if needed)</label>
            <select className={sel} value={coachId} onChange={(e) => setCoachId(e.target.value)} data-testid="schedule-coach">
              <option value="">Assign coach…</option>
              {coaches.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-400 mt-1">
          <input type="checkbox" checked={sendConfirm} onChange={(e) => setSendConfirm(e.target.checked)}
            className="w-4 h-4" data-testid="schedule-send-confirmation" />
          <span data-testid="schedule-confirm-label">
            Confirm the parent straight away{" "}
            {wa && wa.configured && wa.has_confirmation_template
              ? "— the WhatsApp and the email both go out now"
              : "by email (WhatsApp isn't connected yet, so send that from Send / Handoff)"}
            , and hand the trialist to the coach in the portal
          </span>
        </label>
        <DialogFooter><Button onClick={save} disabled={busy} className="bg-cyan-600 hover:bg-cyan-500" data-testid="schedule-save">{busy ? "Saving…" : "Save schedule"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MessagesDialog({ trial, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [parentMsg, setParentMsg] = useState("");
  const [twilio, setTwilio] = useState(null);
  const [tpl, setTpl] = useState(null);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    axios.get(`${API}/academy/trials/${trial.id}/messages`, { withCredentials: true }).then((r) => { setData(r.data); setParentMsg(r.data.parent.text); });
    axios.get(`${API}/academy/trials/twilio-status`, { withCredentials: true }).then((r) => setTwilio(r.data)).catch(() => {});
    axios.get(`${API}/academy/trials/whatsapp-template?trial_id=${trial.id}`, { withCredentials: true })
      .then((r) => setTpl(r.data)).catch(() => {});
  }, []); // eslint-disable-line
  const emailParent = async () => {
    try { await axios.post(`${API}/academy/trials/${trial.id}/send-confirmation`, { channel: "email", message: parentMsg }, { withCredentials: true }); toast.success("Confirmation emailed"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const markSent = async () => { try { await axios.post(`${API}/academy/trials/${trial.id}/send-confirmation`, { channel: "whatsapp", message: parentMsg }, { withCredentials: true }); onDone(); } catch (e) {} };
  const autoSend = async () => {
    setSending(true);
    try { await axios.post(`${API}/academy/trials/${trial.id}/send-confirmation`, { channel: "twilio_whatsapp" }, { withCredentials: true }); toast.success("WhatsApp confirmation sent automatically"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to send"); }
    finally { setSending(false); }
  };
  const autoReady = twilio?.configured && twilio?.has_confirmation_template;
  if (!data) return null;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
        <DialogHeader><DialogTitle>Send / Handoff — {trial.child_name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <p className="text-sm font-semibold mb-1 text-cyan-300">Coach handoff</p>
            <pre className="text-xs bg-slate-800 rounded p-2 whitespace-pre-wrap" data-testid="coach-handoff-text">{data.coach.text}</pre>
            <div className="flex gap-2 mt-2">
              <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-500" disabled={!data.coach.phone} onClick={() => openWa(data.coach.phone, data.coach.text)} data-testid="coach-whatsapp"><MessageCircle className="w-3 h-3 mr-1" />WhatsApp coach</Button>
              <Button size="sm" variant="outline" className="h-7 text-xs border-slate-600" onClick={() => copy(data.coach.text)} data-testid="coach-copy"><Copy className="w-3 h-3 mr-1" />Copy</Button>
            </div>
            {!data.coach.phone && <p className="text-xs text-amber-400 mt-1">No phone on the coach's profile — use Copy.</p>}
          </div>
          <div>
            <p className="text-sm font-semibold mb-1 text-cyan-300">Parent confirmation (editable)</p>
            <Textarea rows={8} className="bg-slate-800 border-slate-700 text-xs" value={parentMsg} onChange={(e) => setParentMsg(e.target.value)} data-testid="parent-msg-text" />
            <div className="flex flex-wrap gap-2 mt-2">
              {autoReady && (
                <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-500" disabled={!data.parent.phone || sending} onClick={autoSend} data-testid="parent-auto-whatsapp"><Zap className="w-3 h-3 mr-1" />{sending ? "Sending…" : "Send auto (WhatsApp)"}</Button>
              )}
              <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-500" disabled={!data.parent.phone} onClick={() => { openWa(data.parent.phone, parentMsg); markSent(); }} data-testid="parent-whatsapp"><MessageCircle className="w-3 h-3 mr-1" />WhatsApp parent</Button>
              <Button size="sm" variant="outline" className="h-7 text-xs border-slate-600" onClick={emailParent} data-testid="parent-email"><Mail className="w-3 h-3 mr-1" />Email parent</Button>
              <Button size="sm" variant="outline" className="h-7 text-xs border-slate-600" onClick={() => copy(parentMsg)} data-testid="parent-copy"><Copy className="w-3 h-3 mr-1" />Copy</Button>
            </div>
            {autoReady && <p className="text-[11px] text-emerald-400/80 mt-1">Auto-send uses your approved WhatsApp template (edits above apply to the manual/copy/email options).</p>}
            {tpl?.filled?.body && (
              <div className="mt-2 rounded border border-emerald-700/40 bg-emerald-500/5 p-2"
                data-testid="trial-whatsapp-template-preview">
                <p className="text-[11px] text-emerald-300 font-semibold">
                  What the automatic WhatsApp sends{tpl.template?.friendly_name ? ` · ${tpl.template.friendly_name}` : ""}
                </p>
                <pre className="text-[11px] text-slate-200 whitespace-pre-wrap mt-1">{tpl.filled.body}</pre>
                <p className="text-[10px] text-slate-500 mt-1">
                  Approved by WhatsApp, so the wording is fixed — the editable text above is used for
                  the email, the manual WhatsApp and Copy.
                </p>
              </div>
            )}
            {tpl?.error && (
              <p className="text-[11px] text-amber-400 mt-1" data-testid="trial-whatsapp-template-error">
                Automatic WhatsApp unavailable: {tpl.error}
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NoteDialog({ trial, onClose, onDone }) {
  const [text, setText] = useState("");
  const save = async () => {
    if (!text.trim()) return;
    try { await axios.post(`${API}/academy/trials/${trial.id}/note`, { text }, { withCredentials: true }); toast.success("Note added"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white">
        <DialogHeader><DialogTitle>Add note — {trial.child_name}</DialogTitle></DialogHeader>
        <Textarea rows={4} className="bg-slate-800 border-slate-700" placeholder="e.g. Parent called, wants to move to next Saturday" value={text} onChange={(e) => setText(e.target.value)} data-testid="note-text" />
        <DialogFooter><Button onClick={save} className="bg-cyan-600 hover:bg-cyan-500" data-testid="note-save">Save note</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OutcomeDialog({ trial, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [newDate, setNewDate] = useState(trial.trial_date || "");
  const act = async (stageVal) => {
    try { await axios.post(`${API}/academy/trials/${trial.id}/outcome`, { stage: stageVal, note, new_date: stageVal === "postponed" ? newDate : undefined }, { withCredentials: true }); toast.success(`Marked ${stageVal.replace("_", " ")}`); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white">
        <DialogHeader><DialogTitle>Update outcome — {trial.child_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Textarea rows={2} className="bg-slate-800 border-slate-700" placeholder="Optional note" value={note} onChange={(e) => setNote(e.target.value)} data-testid="outcome-note" />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500" onClick={() => act("attended")} data-testid="outcome-attended">Attended</Button>
            <Button size="sm" className="bg-red-600 hover:bg-red-500" onClick={() => act("no_show")} data-testid="outcome-no_show">No-show</Button>
            <Button size="sm" className="bg-teal-600 hover:bg-teal-500" onClick={() => act("followed_up")} data-testid="outcome-followed_up">Followed up</Button>
            <Button size="sm" className="bg-green-700 hover:bg-green-600" onClick={() => act("converted")} data-testid="outcome-converted">Converted</Button>
            <Button size="sm" variant="outline" className="border-slate-600" onClick={() => act("lost")} data-testid="outcome-lost">Lost</Button>
          </div>
          <div className="pt-2 border-t border-slate-700">
            <label className="text-xs text-slate-400">Postpone to</label>
            <div className="flex gap-2">
              <Input type="date" className="bg-slate-800 border-slate-700" value={newDate} onChange={(e) => setNewDate(e.target.value)} data-testid="outcome-new-date" />
              <Button size="sm" className="bg-amber-600 hover:bg-amber-500" onClick={() => act("postponed")} data-testid="outcome-postponed">Postpone</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SurveyDialog({ trial, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [twilio, setTwilio] = useState(null);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    axios.get(`${API}/academy/trials/${trial.id}/survey`, { withCredentials: true }).then((r) => setData(r.data));
    axios.get(`${API}/academy/trials/twilio-status`, { withCredentials: true }).then((r) => setTwilio(r.data)).catch(() => {});
  }, []); // eslint-disable-line
  if (!data) return null;
  const url = `${window.location.origin}${data.survey_path}`;
  const msg = data.message_template
    ? data.message_template.replace("{link}", url)
    : `${data.message}${url}`;
  const autoReady = twilio?.configured && twilio?.has_survey_template;
  const emailSurvey = async () => { try { await axios.post(`${API}/academy/trials/${trial.id}/send-survey`, { channel: "email", survey_url: url }, { withCredentials: true }); toast.success("Survey emailed"); onDone(); } catch (e) { toast.error("Failed"); } };
  const waSurvey = async () => { openWa(data.parent_phone, msg); try { await axios.post(`${API}/academy/trials/${trial.id}/send-survey`, { channel: "whatsapp", survey_url: url }, { withCredentials: true }); } catch (e) {} onDone(); };
  const autoSurvey = async () => {
    setSending(true);
    try { await axios.post(`${API}/academy/trials/${trial.id}/send-survey`, { channel: "twilio_whatsapp", survey_url: url }, { withCredentials: true }); toast.success("Survey sent automatically via WhatsApp"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to send"); }
    finally { setSending(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white">
        <DialogHeader><DialogTitle>Post-trial survey — {trial.child_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {data.responded_at ? (
            <div data-testid="survey-responses">
              <Badge className="bg-emerald-500/20 text-emerald-300">Responded</Badge>
              <pre className="text-xs bg-slate-800 rounded p-2 mt-2 whitespace-pre-wrap">{JSON.stringify(data.responses, null, 2)}</pre>
            </div>
          ) : <p className="text-xs text-slate-400">No response yet. Send the survey to the parent:</p>}
          <p className="text-xs bg-slate-800 rounded p-2 break-all" data-testid="survey-link">{url}</p>
          <div>
            <p className="text-[11px] text-slate-500 mb-1">What the parent gets</p>
            <pre className="text-[11px] text-slate-300 bg-slate-950 border border-slate-800 rounded p-2 whitespace-pre-wrap"
              data-testid="survey-message-preview">{msg}</pre>
          </div>
          <div className="flex flex-wrap gap-2">
            {autoReady && (
              <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-500" disabled={!data.parent_phone || sending} onClick={autoSurvey} data-testid="survey-auto-whatsapp"><Zap className="w-3 h-3 mr-1" />{sending ? "Sending…" : "Send auto"}</Button>
            )}
            <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-500" disabled={!data.parent_phone} onClick={waSurvey} data-testid="survey-whatsapp"><MessageCircle className="w-3 h-3 mr-1" />WhatsApp</Button>
            <Button size="sm" variant="outline" className="h-7 text-xs border-slate-600" onClick={emailSurvey} data-testid="survey-email"><Mail className="w-3 h-3 mr-1" />Email</Button>
            <Button size="sm" variant="outline" className="h-7 text-xs border-slate-600" onClick={() => copy(url)} data-testid="survey-copy"><Copy className="w-3 h-3 mr-1" />Copy link</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function ageFrom(dob) {
  if (!dob) return "";
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return "";
  const years = Math.floor((Date.now() - d.getTime()) / 31557600000);
  return years > 0 && years < 25 ? `${years} yrs` : "";
}

function ConvertDialog({ trial, onClose, onDone }) {
  const [terms, setTerms] = useState([]);
  const [termId, setTermId] = useState("");
  const [sessions, setSessions] = useState([]);
  const [picked, setPicked] = useState([]);
  const [fee, setFee] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/academy/terms`, { withCredentials: true })
      .then((r) => setTerms(r.data.terms || r.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!termId) { setSessions([]); return; }
    axios.get(`${API}/academy/terms/${termId}/sessions`, { withCredentials: true })
      .then((r) => setSessions(r.data.sessions || r.data || []))
      .catch(() => setSessions([]));
    setPicked([]);
  }, [termId]);

  const go = async () => {
    if (!termId || picked.length === 0) { toast.error("Pick the term and at least one session"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/academy/trials/${trial.id}/convert`,
        { term_id: termId, session_ids: picked, charge_registration_fee: fee },
        { withCredentials: true });
      toast.success(`Registered for the term${r.data.registration_id ? ` · ${r.data.registration_id}` : ""}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not convert this trial");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
        <DialogHeader><DialogTitle>Register for the term — {trial.child_name}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-400">
          Uses the parent and child details from the trial{trial.child_dob ? ` (born ${trial.child_dob})` : ""} and
          creates the registration and invoice just like registering by hand.
        </p>
        <div>
          <label className="text-xs text-slate-400">Term</label>
          <select value={termId} onChange={(e) => setTermId(e.target.value)}
            className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-sm"
            data-testid="convert-term">
            <option value="">Select term…</option>
            {terms.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        {sessions.length > 0 && (
          <div className="max-h-56 overflow-y-auto space-y-1">
            {sessions.map((s) => (
              <label key={s.id} className="flex items-center gap-2 text-xs bg-slate-800/60 rounded px-2 py-1.5"
                data-testid={`convert-session-${s.id}`}>
                <input type="checkbox" checked={picked.includes(s.id)} className="w-4 h-4"
                  onChange={() => setPicked((p) => p.includes(s.id) ? p.filter((x) => x !== s.id) : [...p, s.id])} />
                <span>{s.name || DAY_NAMES[s.day_of_week] || `Day ${s.day_of_week}`} {s.start_time || ""} · ${s.price ?? "—"}</span>
              </label>
            ))}
          </div>
        )}
        <label className="flex items-center gap-2 text-xs text-slate-400">
          <input type="checkbox" checked={fee} onChange={(e) => setFee(e.target.checked)}
            className="w-4 h-4" data-testid="convert-reg-fee" />
          Charge the registration fee
        </label>
        <DialogFooter>
          <Button onClick={go} disabled={busy} className="bg-emerald-600 hover:bg-emerald-500"
            data-testid="convert-confirm">{busy ? "Registering…" : "Create registration"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TemplateEditor({ path, title, blurb, placeholders, testid }) {
  const [tpl, setTpl] = useState(null);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const load = React.useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/whatsapp/${path}`, { withCredentials: true });
      setTpl(r.data);
      setBody(r.data.body || r.data.default_body || "");
    } catch (e) { /* shown as not registered */ }
  }, [path]);
  useEffect(() => { load(); }, [load]);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/whatsapp/${path}`,
        { body, force: !!tpl?.registered }, { withCredentials: true });
      toast.success(r.data.already ? `Already submitted · ${r.data.status}`
        : `Sent to WhatsApp for approval · ${r.data.status || "received"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Twilio refused the template");
    } finally { setBusy(false); }
  };
  return (
    <div className="border-t border-slate-800 pt-3" data-testid={`${testid}-card`}>
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-sm text-white">{title}</p>
        <Badge className="text-[10px] bg-slate-700 text-slate-200" data-testid={`${testid}-status`}>
          {tpl?.status || (tpl?.registered ? "pending" : "not registered")}
        </Badge>
      </div>
      <p className="text-xs text-slate-400 mt-1">{blurb}</p>
      <Textarea rows={6} value={body} onChange={(e) => setBody(e.target.value)}
        className="bg-slate-950 border-slate-800 text-[11px] mt-2 font-mono"
        data-testid={`${testid}-body`} />
      <p className="text-[11px] text-slate-500 mt-1">{placeholders}</p>
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <Button size="sm" onClick={submit} disabled={busy}
          className="h-8 text-[11px] bg-sky-600 hover:bg-sky-500" data-testid={`${testid}-register`}>
          {tpl?.registered ? "Submit this wording" : "Register with WhatsApp"}
        </Button>
        <Button size="sm" variant="ghost" className="h-8 text-[11px] text-slate-300"
          onClick={load} data-testid={`${testid}-refresh`}>Check approval</Button>
        <Button size="sm" variant="ghost" className="h-8 text-[11px] text-slate-400"
          onClick={() => setBody(tpl?.default_body || "")} data-testid={`${testid}-reset`}>
          Use recommended wording
        </Button>
      </div>
      {tpl?.rejection_reason && (
        <p className="text-[11px] text-red-300 mt-1">WhatsApp said: {tpl.rejection_reason}</p>
      )}
    </div>
  );
}

function MessageNotesDialog({ onClose }) {
  const [notes, setNotes] = useState("");
  const [autoSurvey, setAutoSurvey] = useState(true);
  const [busy, setBusy] = useState(false);
  const runReminders = async () => {
    try {
      const r = await axios.post(`${API}/academy/trials/reminders/run`, {}, { withCredentials: true });
      toast.success(`${r.data.sent} of ${r.data.due} reminder(s) sent for tomorrow`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not run reminders");
    }
  };
  const runSurveys = async () => {
    try {
      const r = await axios.post(`${API}/academy/trials/surveys/run`, {}, { withCredentials: true });
      if (r.data.skipped) {
        toast.message(r.data.reason === "auto_survey_off"
          ? "Automatic surveys are switched off"
          : "Surveys go out between 8am and midday — nothing sent now");
      } else {
        toast.success(`${r.data.sent} of ${r.data.due} survey(s) sent`
          + (r.data.skipped_no_attendance ? ` · ${r.data.skipped_no_attendance} skipped, not marked present` : "")
          + (r.data.could_not_reach ? ` · ${r.data.could_not_reach} with no phone or email we could reach` : ""));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not run surveys");
    }
  };
  useEffect(() => {
    axios.get(`${API}/academy/trials/settings`, { withCredentials: true })
      .then((r) => {
        setNotes(r.data.confirmation_notes || "");
        setAutoSurvey(r.data.auto_survey_enabled !== false);
      })
      .catch(() => {});
  }, []);
  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/academy/trials/settings`,
        { confirmation_notes: notes, auto_survey_enabled: autoSurvey }, { withCredentials: true });
      toast.success("Saved — added to every trial confirmation");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Trial messages</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-400">
          The seasonal note below is added to the bottom of every trial confirmation, email and
          WhatsApp. Use it for the things that change with the season — haze, wet weather, school
          holidays.
        </p>
        <Textarea rows={4} className="bg-slate-800 border-slate-700"
          placeholder="e.g. If the haze PSI goes above 150 or there is lightning in the area, sessions are called off and we will message you by 3pm on the day."
          value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="trial-notes-input" />

        <TemplateEditor
          path="trial-template" testid="trial-template"
          title="Trial confirmation (WhatsApp)"
          blurb="Sent when you schedule a trial, and again as the day-before reminder."
          placeholders="{{1}} parent · {{2}} child · {{3}} date & time · {{4}} venue · {{5}} directions · {{6}} parking — all six must stay in the wording."
        />
        <TemplateEditor
          path="survey-template" testid="survey-template"
          title="Post-trial survey (WhatsApp)"
          blurb="Sent after the trial to ask the parent how it went."
          placeholders="{{1}} parent · {{2}} child · {{3}} survey link — all three must stay in the wording."
        />
        <TemplateEditor
          path="cancellation-template" testid="cancellation-template-trials"
          title="Rain / haze cancellation (WhatsApp)"
          blurb="Goes out automatically to every family booked that day, including trialists scheduled for it."
          placeholders="{{1}} parent · {{2}} child · {{3}} date · {{4}} reason · {{5}} closing line (trialists are asked to reply REBOOK)."
        />

        <div className="border-t border-slate-800 pt-3 space-y-2">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm text-white">Send the survey the morning after</p>
              <p className="text-xs text-slate-400 mt-0.5">
                Goes out between 8am and midday to trialists the register marked
                <span className="text-slate-200"> present</span> the day before — nobody else, and
                never twice.
              </p>
            </div>
            <Switch checked={autoSurvey} onCheckedChange={setAutoSurvey}
              data-testid="auto-survey-toggle" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" className="h-8 text-[11px] border-slate-600"
              onClick={runReminders} data-testid="trial-reminders-run">Send tomorrow&apos;s reminders now</Button>
            <Button size="sm" variant="outline" className="h-8 text-[11px] border-slate-600"
              onClick={runSurveys} data-testid="trial-surveys-run">Send yesterday&apos;s surveys now</Button>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={save} disabled={busy} className="bg-cyan-600 hover:bg-cyan-500" data-testid="trial-notes-save">
            Save note
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeclineDialog({ trial, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/academy/trials/${trial.id}/decline`,
        { reason, notify }, { withCredentials: true });
      toast.success(r.data.parent_emailed ? "Declined · parent emailed" : "Declined");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not decline");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
        <DialogHeader><DialogTitle>Decline trial — {trial.child_name}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-400">
          The parent is told we can&apos;t offer a slot right now and that we&apos;ll be in touch if
          one opens up. Your reason is included if you add one.
        </p>
        <Textarea rows={3} className="bg-slate-800 border-slate-700"
          placeholder="e.g. the U8 group is full this term" value={reason}
          onChange={(e) => setReason(e.target.value)} data-testid="trial-decline-reason" />
        <label className="flex items-center gap-2 text-xs text-slate-400">
          <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)}
            className="w-4 h-4" data-testid="trial-decline-notify" />
          Email the parent
        </label>
        <DialogFooter>
          <Button onClick={go} disabled={busy} className="bg-rose-600 hover:bg-rose-500"
            data-testid="trial-decline-confirm">Decline trial</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VenuesDialog({ onClose }) {
  const [venues, setVenues] = useState([]);
  useEffect(() => { axios.get(`${API}/academy/trial-venues`, { withCredentials: true }).then((r) => setVenues(r.data.venues.length ? r.data.venues : [{ name: "", address: "", maps_link: "", parking_notes: "" }])); }, []); // eslint-disable-line
  const upd = (i, f, v) => setVenues((p) => p.map((x, idx) => idx === i ? { ...x, [f]: v } : x));
  const save = async () => { try { await axios.put(`${API}/academy/trial-venues`, { venues }, { withCredentials: true }); toast.success("Venues saved"); onClose(); } catch (e) { toast.error("Failed"); } };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
        <DialogHeader><DialogTitle>Trial venues</DialogTitle></DialogHeader>
        <div className="space-y-4 max-h-[60vh] overflow-y-auto">
          {venues.map((v, i) => (
            <div key={i} className="space-y-2 border border-slate-700 rounded p-3" data-testid={`venue-row-${i}`}>
              <Input className="bg-slate-800 border-slate-700" placeholder="Venue name" value={v.name} onChange={(e) => upd(i, "name", e.target.value)} data-testid={`venue-name-${i}`} />
              <Input className="bg-slate-800 border-slate-700" placeholder="Address" value={v.address} onChange={(e) => upd(i, "address", e.target.value)} data-testid={`venue-address-${i}`} />
              <Input className="bg-slate-800 border-slate-700" placeholder="Google Maps link" value={v.maps_link} onChange={(e) => upd(i, "maps_link", e.target.value)} data-testid={`venue-maps-${i}`} />
              <Textarea rows={2} className="bg-slate-800 border-slate-700" placeholder="Parking notes / directions (sent to parents)" value={v.parking_notes} onChange={(e) => upd(i, "parking_notes", e.target.value)} data-testid={`venue-parking-${i}`} />
            </div>
          ))}
          <Button size="sm" variant="outline" className="border-slate-600" onClick={() => setVenues((p) => [...p, { name: "", address: "", maps_link: "", parking_notes: "" }])} data-testid="venue-add">+ Add venue</Button>
        </div>
        <DialogFooter><Button onClick={save} className="bg-cyan-600 hover:bg-cyan-500" data-testid="venues-save">Save venues</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
